import React, { FC } from 'react';
import { useSelector } from 'react-redux';
import { Info } from '@mui/icons-material';
import makeStyles from '@mui/styles/makeStyles';
import { isNullOrUndefined } from 'utils/typeGuargs';
import { Box, Tooltip, Typography } from '@mui/material';
import { FACT_NAMES_NUMERIC, FACT_NAMES_STRING } from '@trnsact/business-criteria';
import { TEST_IDS } from 'shared';
import { deskingSelectors } from '../../../model';
import { BooleanField, ButtonAction, NumericField, StringField, InServiceDateField } from './index';
import {
  factsKeyMapToFieldLabel,
  factsExcludeEquipmentEntity,
  requiredOptionsForDynamicFields,
} from '../../../constants';
import {
  Fact,
  FactToCheckTypes,
  ProductConfigFactValue,
  EquipmentDynamicFieldsProps,
  ProductsDynamicFieldsRender,
  ProposalProductCoverageTermDefaultToFinanceTermOption,
} from '../../../types';

interface Props {
  onChangeAllProductsConfig: (configField: string, nextValue: ProductConfigFactValue) => void;
}

export const ProductsDynamicFields = ({ onChangeAllProductsConfig }: Props) => {
  const classes = useStyles();

  const productsFactsForCheckArray = useSelector(deskingSelectors.productsFactsForCheckArray);
  const commonConfig = useSelector(deskingSelectors.commonProductsConfiguration);
  const isNeedRenderDynamicFields = useSelector(deskingSelectors.isNeedRenderDynamicFields);

  if (!isNeedRenderDynamicFields) return null;

  const componentsByType: Record<FactToCheckTypes, FC<EquipmentDynamicFieldsProps>> = {
    string: StringField,
    numeric: NumericField,
    boolean: BooleanField,
  };

  const specificManagementComponents: Record<Fact, FC<EquipmentDynamicFieldsProps>> = {
    [FACT_NAMES_STRING.COVERAGE_TERM]: ButtonAction,
    [FACT_NAMES_NUMERIC.IN_SERVICE_DATE]: InServiceDateField,
  };

  const renderConfig = productsFactsForCheckArray.reduce<ProductsDynamicFieldsRender>(
    (acc, fact) => {
      factsExcludeEquipmentEntity.forEach(targetFactKey => {
        if (fact.factKey === targetFactKey) {
          acc.meta[targetFactKey] = {
            options: [
              ...(acc.meta[targetFactKey]?.options ?? []),
              ...(fact.options ?? []),
              ...(requiredOptionsForDynamicFields?.[targetFactKey] ?? []),
            ],
          };
        }

        if (fact.factKey === targetFactKey && !acc.render[targetFactKey]) {
          acc.render[targetFactKey] = {
            fact,
            fieldComponent: specificManagementComponents[fact.factKey] ?? componentsByType[fact.type],
          };
        }

        return acc;
      });

      return acc;
    },
    { render: {}, meta: {} }
  );

  return (
    <Box className={classes.container}>
      <Box className="labelContainer">
        <Typography component="span" variant="subtitle2">
          Product Configuration Required
        </Typography>

        <Tooltip title="All values will be apply for all selected products">
          <Info color="primary" fontSize="small" />
        </Tooltip>
      </Box>

      <Box className="fieldsContainer">
        {Object.entries(renderConfig.render).map(([factType, { fieldComponent: FieldComponent, fact }]) => {
          const value = commonConfig?.[fact.factKey] ?? '';

          return (
            <FieldComponent
              fact={fact}
              value={value}
              key={factType}
              helperText="Required"
              dataTestId={TEST_IDS.desking.productConfiguration.field(fact.factKey)}
              label={factsKeyMapToFieldLabel[fact.factKey]}
              error={value === '' || isNullOrUndefined(value)}
              tooltip="Clicking this will set the coverage term for all products to the Finance Term of the deal. If there is no exact match, the closest term is selected."
              onChange={nextValue => onChangeAllProductsConfig(fact.factKey, nextValue)}
              onClick={fact => {
                onChangeAllProductsConfig(fact.factKey, ProposalProductCoverageTermDefaultToFinanceTermOption);
              }}
              options={Array.from(new Set(renderConfig.meta[fact.factKey].options)).map(value => ({
                label: value,
                value,
              }))}
            />
          );
        })}
      </Box>
    </Box>
  );
};

const useStyles = makeStyles({
  container: {
    gap: '0.5rem',
    display: 'flex',
    flexDirection: 'column',

    '& .labelContainer': {
      gap: '1rem',
      display: 'flex',
      alignItems: 'center',
    },

    '& .fieldsContainer': {
      gap: '0.5rem',
      display: 'grid',
      gridTemplateColumns: 'repeat(3, 1fr)',
    },
  },
});
